<template>

	<div class="body-child">

		<div class="about-background"></div>

		<div class="content main">

			<picture class="profile-picture">
				<source srcset="/src/img/me.webp" type="image/webp"/>
				<source srcset="/src/img/me.jpg" type="image/jpg"/>
				<img src="/src/img/me.jpg" alt=""/>
			</picture>

			<div class="text">

				<h1 style="margin-top: 0;">Who am I?</h1>

				<p>
					Hi! I’m <Accent>Kylian Dekker</Accent>,
					a {{ AGE() }}-year old gameplay programmer from the Netherlands
					with <Accent>{{ PROGRAMMING_EXPERIENCE_AAA() }}</Accent>
					years of professional experience in the AAA industry and <Accent>2</Accent> years in Indie Games.
				</p>

				<p>
					I started programming in {{ PROGRAMMING_YEAR() }}
					and I studied indie game programming at
					<Accent>SintLucas Eindhoven</Accent>
					and later specialized in AAA development at
					<Accent>Breda University of Applied Sciences</Accent>
					(formerly <Accent>NHTV</Accent>),
					graduating with a
					<Accent>Cum Laude/First Class Bachelor of Science (Hons)</Accent>.
				</p>

				<p>
					In 2018, I won the
					<Accent>INDIGO Award for Best Indie Game</Accent>
					for my game named <Accent>CoopScoop</Accent>.
				</p>

				<p>Some of the most important titles I have worked on thus far are:</p>

				<ul>
					<li>Microsoft Flight Simulator 2024</li>
					<li>Arma 4</li>
					<li>Unannounced Title in DayZ Series</li>
				</ul>

				<div class="content block">

					<div class="text">

						<h3>Programming Languages</h3>

						<ul style="text-align: left;">
							<li><Icon :icon="'cpp'"></Icon> C++</li>
							<li><Icon :icon="'csharp'"></Icon> C#</li>
							<li><Icon :icon="'js'"></Icon> Javascript/Typescript/Vue.js</li>
						</ul>

					</div>

				</div>

				<div class="content">

					<div class="text">

						<h3>Programming Software</h3>

						<ul style="text-align: left;">
							<li><Icon :icon="'git'"></Icon> Git</li>
							<li><Icon :icon="'perforce'"></Icon> Perforce</li>
							<li><Icon :icon="'unity'"></Icon> Unity Engine</li>
							<li><Icon :icon="'visual-studio'"></Icon> Microsoft Visual Studio</li>
							<li><Icon :icon="'visual-studio-code'"></Icon> Microsoft Visual Studio Code</li>
						</ul>

					</div>

				</div>

				<div class="content block">

					<div class="text">

						<h3>Platforms</h3>

						<ul style="text-align: left;">
							<li><Icon :icon="'windows'"></Icon> Windows</li>
							<li><Icon :icon="'playstation'"></Icon> Playstation 4</li>
							<li><Icon :icon="'playstation'"></Icon> Playstation 5</li>
							<li><Icon :icon="'vr'"></Icon> Oculus and SteamVR</li>
						</ul>

					</div>

				</div>

				<div class="content">

					<div class="text">

						<h3>Languages</h3>

						<ul style="text-align: left;">
							<li>Dutch</li>
							<li>English</li>
						</ul>

					</div>

				</div>

			</div>

		</div>

		<div class="timeline">

			<div v-for="event in events" :key="event.name" class="timeline-item">

				<div class="timeline-dot"></div>

				<div class="timeline-content">

					<img
						class="timeline-image"
						:src="event.logo"
						alt="Image did not load. ;/"
					/>

					<h3>{{ event.getDateFormatted() }} {{ event.getTimeSince() }}</h3>

					<h2>{{ event.name }}</h2>

					<component :is="event.description" />

				</div>

			</div>

		</div>

	</div>

</template>

<script lang="ts">
import { defineComponent } from "vue";

import Icon from "./Icon/Icon.vue";
import Accent from "@/components/Accent/Accent.vue";
import { h, type VNode } from "vue";

import {
	PROGRAMMING_YEAR,
	PROGRAMMING_EXPERIENCE_AAA,
	AGE
} from "@/data";

enum EventCategory
{
	JOB,
	EDUCATION,
	AWARD
}

class Event
{
	public name: string;
	public description: () => VNode;
	public logo: string;
	public startDate: Date;
	public endDate: Date;
	public category: EventCategory;

	constructor(
		name: string,
		description: () => VNode,
		logo: string,
		startDate: Date,
		category: EventCategory,
		endDate: Date = new Date(),
	)
	{
		this.name = name;
		this.description = description;
		this.logo = logo;
		this.startDate = startDate;
		this.category = category;
		this.endDate = endDate;
	}

	public getFormattedStartDate(): string
	{
		return this.startDate.toLocaleDateString("en-US", {
			month: "long",
			year: "numeric"
		});
	}

	public getFormattedEndDate(): string
	{
		if (this.endDate == new Date())
		{
			return "Present";
		}
		return this.endDate.toLocaleDateString("en-US", {
			month: "long",
			year: "numeric"
		});
	}

	public getDateFormatted(): string
	{
		var startDateStr = this.getFormattedStartDate();
		var endDateStr = this.getFormattedEndDate();

		if (startDateStr === endDateStr)
		{
			return startDateStr;
		}
		return startDateStr + " - " + endDateStr;
	}

	public getTimeSince(): string
	{
		const months: number =
		(this.endDate.getFullYear() - this.startDate.getFullYear()) * 12 +
		(this.endDate.getMonth() - this.startDate.getMonth());

		const years: number = Math.floor(months / 12);
		const remainingMonths: number = months % 12;

		if (years === 0 && months === 0)
		{
			return ``;
		}

		if (years === 0)
		{
			return `(${remainingMonths} ${remainingMonths === 1 ? "month" : "months"})`;
		}

		if (remainingMonths === 0)
		{
			return `(${years} ${years === 1 ? "year" : "years"})`;
		}

		return `(${years} ${years === 1 ? "year" : "years"} and ${remainingMonths} ${remainingMonths === 1 ? "month" : "months"})`;
	}
}

const events: Event[] = [
	new Event(
		"Bohemia Interactive",
		() => h(
			"p",
			null,
			[
				"Gameplay Programmer working on ",
				h(Accent, null, { default: () => "Arma 4" }),
				" and an ",
				h(Accent, null, { default: () => "unannounced title in the DayZ series" }),
				"."
			]
		),
		"https://imgeucdn.gamespress.com/cdn/files/LevelInfinite/2024/08/141031-317dd646/Bohemia_Interactive_logo_green_only.png?w=700&mode=max&otf=y&quality=90&format=png&bgcolor=transparent&sky=09897d40b5d3c5446344c5fa16f5e2cb0e90cc1ed042414c97bbe386b1a57f33",
		new Date(2025, 5, 1),
		EventCategory.JOB
	),
	new Event(
		"Climax Studios",
		() => h(
			"p",
			null,
			[
				"Gameplay Programmer working on ",
				h(Accent, null, { default: () => "Microsoft Flight Simulator 2024" }),
				" on ",
				h(Accent, null, { default: () => "Windows" }),
				" and ",
				h(Accent, null, { default: () => "PS5" }),
				".",
			]
		),
		"https://infinitgamer.com/images/2017/05/16/climex.png",
		new Date(2023, 9, 1),
		EventCategory.JOB,
		new Date(2024, 9, 1)
	),
	new Event(
		"Graduated",
		() => h(
			"p",
			null,
			[
				"Graduated ",
				h(Accent, null, { default: () => "Cum Laude" }),
				"/",
				h(Accent, null, { default: () => "with Honors" }),
				" with a ",
				h(Accent, null, { default: () => "Bachelor's degree" }),
				" in ",
				h(Accent, null, { default: () => "Creative Media and Game Technologies" }),
				".",
			]
		),
		"https://upload.wikimedia.org/wikipedia/en/thumb/5/51/Breda_University_of_Applied_Sciences_Logo.svg/1280px-Breda_University_of_Applied_Sciences_Logo.svg.png",
		new Date(2024, 7, 1),
		EventCategory.EDUCATION,
		new Date(2024, 7, 1)
	),
	new Event(
		"MOONLANDER.AI",
		() => h(
			"p",
			null,
			[
				h(Accent, null, { default: () => "Generalist Programmer" }),
				" on an ",
				h(Accent, null, { default: () => "AI-Based Game Creation Tool" }),
				" called ",
				h(Accent, null, { default: () => "ShapeShifter" }),
				".",
			]
		),
		"https://daleelai.com/wp-content/uploads/2025/02/logo1740576459.png",
		new Date(2022, 7, 1),
		EventCategory.JOB,
		new Date(2023, 9, 1)
	),
	new Event(
		"Enrollment in HBO",
		() => h(
			"p",
			null,
			[
				"Started (and finished) an education with specialization in ",
				h(Accent, null, { default: () => "Gameplay Programming" }),
				".",
			]
		),
		"https://upload.wikimedia.org/wikipedia/en/thumb/5/51/Breda_University_of_Applied_Sciences_Logo.svg/1280px-Breda_University_of_Applied_Sciences_Logo.svg.png",
		new Date(2020, 7, 1),
		EventCategory.EDUCATION,
		new Date(2024, 7, 1)
	),
	new Event(
		"Graduated",
		() => h(
			"p",
			null,
			[
				"Graduated as a ",
				h(Accent, null, { default: () => "Game Developer" }),
				".",
			]
		),
		"https://opleiding.com/wp-content/uploads/opleider/sint-lucas.png",
		new Date(2016, 7, 1),
		EventCategory.EDUCATION,
		new Date(2016, 7, 1)
	),
	new Event(
		"LABEL4Visuals",
		() => h(
			"p",
			null,
			[
				h(Accent, null, { default: () => "Gameplay Programmer" }),
				" on ",
				h(Accent, null, { default: () => "VR" }),
				" and ",
				h(Accent, null, { default: () => "AR" }),
				" projects for various companies.",
			]
		),
		"https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSV9lO4fFkXzf_9tPyenyndyztFWhpW_gCluw&s",
		new Date(2018, 8, 1),
		EventCategory.JOB,
		new Date(2019, 1, 1)
	),
	new Event(
		"Indigo Award for Best Indie Game",
		() => h(
			"p",
			null,
			[
				"Won the ",
				h(Accent, null, { default: () => "Indigo Award" }),
				" for Best Indie Game with a game called \"",
				h(Accent, null, { default: () => "CoopScoop" }),
				"\"."
			]
		),
		"https://www.indigoshowcase.nl/images/og/INDIGO-og-default.png",
		new Date(2018, 7, 1),
		EventCategory.AWARD,
		new Date(2018, 7, 1)
	),
	new Event(
		"Enrollment in MBO",
		() => h(
			"p",
			null,
			[
				"Started (and finished) an education with specialization for ",
				h(Accent, null, { default: () => "Game Developer" }),
				".",
			]
		),
		"https://opleiding.com/wp-content/uploads/opleider/sint-lucas.png",
		new Date(2020, 7, 1),
		EventCategory.EDUCATION,
		new Date(2024, 7, 1)
	),
];

import EventBus from "../EventBus";

export default defineComponent({
	name: "AboutMe",

	components: {
		Icon,
		Accent
	},

	setup() {

		EventBus.accentColor = "rgb(72, 199, 240)";
		EventBus.accentBold = true;

		return {
			AGE,
			PROGRAMMING_YEAR,
			PROGRAMMING_EXPERIENCE_AAA,
			events : events
		};
	}
});
</script>

<style lang="scss" scoped>

@import url("/public/src/css/default.scss");
@import url("/public/src/css/about.scss");

</style>